/**
 * PL Collection Page Renderer
 * Renders collection pages using widgetized PL format
 * Maps PL widgets to React components
 */
import { Pagination } from '@lastbrand/storefront-proto-registry/api/v1/common'
import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { usePLPageStateCapture } from '@src/components/pl-base-widget/hooks/use-pl-page-state-capture'
import { usePLScrollRestoration } from '@src/components/pl-base-widget/hooks/use-pl-scroll-restoration'
import { PLPageMeta, PLPageProvider } from '@src/components/pl-page-context'
import { LayoutPaginationWrapper } from '@src/components/pl-page-renderer/layout-pagination-wrapper'
import { absolutizeCanonical } from '@src/components/seo/canonical.utils'
import { CollectionPageSchema } from '@src/components/seo/collection-page-schema'
import { SeoPageRouter } from '@src/components/seo/seo-page-router'
import { getCollectionViewData } from '@src/components/widgets/analyticsUtils'
import { STATSIG_GATE_NAMES, useStatsigGate } from '@src/experiments/statsig-integration/use-statsig-gate.hook'
import { useQuinceRouter } from '@src/modules/navigation'
import { CollectionFeatureProvider } from '@src/pages/[...slug]/components/collection/collection-feature-provider'
import { scrollToProductSection } from '@src/pages/[...slug]/components/collection/collection.helpers'
import { resolveClpRankingDefaultBaseSortBy } from '@src/pages/[...slug]/components/collection/hooks/clp-ranking-base-sort-by.utils'
import {
  consumePendingCollectionProductScroll,
  getCollectionCacheKey,
  getFormattedSlug,
} from '@src/pages/[...slug]/components/collection/utils'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { ResponsiveInteractionsProvider } from '@src/quince-site-common/src/contexts/responsive-interactions-context/responsive-interactions-context'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useMounted from '@src/quince-site-common/src/hooks/useMounted'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import { getSeoMetapageData, type GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'
import { PL_WIDGET_BANDS } from '@src/scroll-restoration/bands'
import { useBackNavScrollRestoration } from '@src/scroll-restoration/use-back-nav-scroll-restoration'
import { PLAPIResponse } from '@src/server-side/api/fetch-pl-data'
import {
  buildCollectionPageSchemaPropsPL,
  resolveSchemaPageName,
} from '@src/server-side/page-data/pl/page-type-handlers/collection-page-schema-data-pl'
import { CollectionServerSideFeatureFlags } from '@src/server-side/server-feature-flags/feature-flag.types'
import { setFindingMethod, FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { trackingMetaData } from '../[...slug]/components/collection/collection.constants'

import { CLPLayoutContainer } from './clp-layout-container'
import { getWidgetRegistry } from './collection-widget-registry'
import { FilterSortProvider } from './contexts/filter-sort-context'
import { useCLPWidgetStateRestoration } from './hooks/use-clp-widget-state-restoration'

import type { SeoData } from '@lastbrand/storefront-proto-registry/api/v1/seo'

export interface CollectionPagePLRendererProps {
  plData: PLAPIResponse
  pageMeta: PLPageMeta
  serverSideFeatureFlags?: CollectionServerSideFeatureFlags | null
}

/**
 * Internal component that uses hooks within PLPageProvider context
 */
function CLPPageHooks({ pageMeta }: { pageMeta: PLPageMeta }) {
  // Capture page state (widget states + layout pagination state) before navigation
  usePLPageStateCapture()

  // Restore widget states from cache
  useCLPWidgetStateRestoration(pageMeta)

  // Restore scroll position after widgets render
  usePLScrollRestoration()

  return null
}

interface CLPLayoutProps {
  plData: PLAPIResponse
  pageMeta: PLPageMeta
  widgetRegistry: ReturnType<typeof getWidgetRegistry>
  initialPagination: Pagination | undefined
}

/**
 * Internal layout component that must be rendered inside CollectionFeatureProvider.
 * Its widgets read CollectionFeatureContext, so it is separated to ensure those reads
 * happen within the provider's context tree.
 */
function CLPLayout({ plData, pageMeta, widgetRegistry, initialPagination }: Readonly<CLPLayoutProps>) {
  // TODO: Temp fix — suppress container top margin when banner is the first widget to avoid
  // double spacing. Ideal fix should come with design migration.
  const isBannerFirst = plData.widgets[0]?.type === WidgetType.COLLECTION_BANNER

  // Back-navigation scroll restoration, DS only (STRFRNT-16138). Legacy traffic keeps the pixel-cache
  // restore in `usePLScrollRestoration`, which stands down under DS — the two are mutually exclusive
  // because both read this same synchronous per-render flag. The hook is always CALLED; only `enabled`
  // varies, since the DS decision is per-page and flips between client navigations. It lives here
  // rather than in `CLPPageHooks` because its result drives this component's wrapper props.
  const backNav = useBackNavScrollRestoration({ bands: PL_WIDGET_BANDS, enabled: shouldUseDesignSystem() })

  // `CLPLayoutContainer` is the `shouldUseDesignSystem()` switcher for the
  // page-level top-margin rule. Legacy variant owns the SCSS module; DS
  // variant is a Panda inline `marginTop: 'space-20'` with no SCSS import.
  return (
    <CLPLayoutContainer isBannerFirst={isBannerFirst}>
      <LayoutPaginationWrapper
        initialWidgets={plData.widgets}
        initialPagination={initialPagination}
        widgetRegistry={widgetRegistry}
        page={pageMeta.seoData?.title ?? ''}
        forceLoadActive={backNav.active}
        forceLoadTargetId={backNav.targetId}
        onForceLoadComplete={backNav.onComplete}
        suppressForceLoadScroll={backNav.active}
      />
    </CLPLayoutContainer>
  )
}

/**
 * PL Collection Page Renderer
 * Renders widgets from PL API response
 *
 * Note: Pagination coordination is now page-scoped via PaginationCoordinatorProvider
 * which is automatically included in PLPageProvider. No manual reset needed.
 *
 * Also handles scroll and widget state restoration:
 * 1. Restores widget states from cache (before widgets render)
 * 2. Captures widget states before navigation
 * 3. Restores scroll position after widgets render
 */
export function CollectionPagePLRenderer(props: Readonly<CollectionPagePLRendererProps>) {
  return (
    <CollectionFeatureProvider serverSideFeatureFlags={props.serverSideFeatureFlags}>
      <CollectionPagePLRendererContent {...props} />
    </CollectionFeatureProvider>
  )
}

function CollectionPagePLRendererContent({
  plData,
  pageMeta,
  serverSideFeatureFlags,
}: Readonly<CollectionPagePLRendererProps>) {
  const widgetRegistry = getWidgetRegistry()
  const router = useQuinceRouter()
  const query = router.query
  const pathname = usePathname()
  const isMounted = useMounted()
  const { zipCode } = useZipCode()
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  const isI18nPreviewEnabled = useI18nPreviewContext()
  const { locale, regionDetails } = useLocaleContext()
  const { t } = useTranslation('common')
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  // Read once at the page root and provide to every widget below (filters, subcollection
  // cards, product grid) — the product cards' snappy-navigation gate lives on this too, so
  // the provider must sit ABOVE the grid widget, not inside filter-and-sort-widget.
  const optimisticPaint = useStatsigGate(STATSIG_GATE_NAMES.CLP_RESPONSIVE_FILTERS)

  // Path mode: the path is canonical for the active subcollection, so SeoPageRouter is redundant.
  // The legacy key preserves the mount across subcoll hops; the clp_subcollection_path_remount
  // gate below overrides that to remount per subcollection. Query mode keys by pathname (subcoll
  // state lives in the filter query) and needs SeoPageRouter for dynamic SEO updates.
  const isPathSubcollectionMode = serverSideFeatureFlags?.subCollectionUrlPlacement === 'path'

  // Gate ON remounts per subcollection (pathname key), fixing widget-pagination state bleeding
  // across subcollections in path mode. OFF keeps the legacy base-collection key.
  const subcollectionRemountEnabled = useStatsigGate(STATSIG_GATE_NAMES.CLP_SUBCOLLECTION_PATH_REMOUNT)

  const baseCollectionKey = pageMeta.pageMetaData?.slug ?? pageMeta.pathname
  const legacyProviderKey = isPathSubcollectionMode ? baseCollectionKey : pageMeta.pathname
  const providerKey = subcollectionRemountEnabled ? pageMeta.pathname : legacyProviderKey

  // Dynamic SEO state — driven by FilterSortProvider's onSeoDataUpdate in query mode.
  const [dynamicSeoData, setDynamicSeoData] = useState<SeoData | undefined>(undefined)
  const onSeoDataUpdate = useCallback((seoData: SeoData | undefined) => {
    setDynamicSeoData(seoData)
  }, [])

  // Reset dynamic SEO data when navigating to a different route
  useEffect(() => {
    setDynamicSeoData(undefined)
  }, [pathname])

  const dynamicSeoMetadata = useMemo(() => {
    if (!dynamicSeoData) return null
    const seoProps: GetSeoMetapageDataProps = {
      title: dynamicSeoData.title || undefined,
      description: dynamicSeoData.description || undefined,
      imageUrl: dynamicSeoData.imageUrl || undefined,
      canonicalUrl: absolutizeCanonical(dynamicSeoData.canonicalUrl),
      noindex: dynamicSeoData.noindex,
      nofollow: dynamicSeoData.nofollow,
      slug: pageMeta.seoData?.slug,
    }
    return getSeoMetapageData(isI18nPreviewEnabled, seoProps, locale, regionDetails)
  }, [dynamicSeoData, isI18nPreviewEnabled, pageMeta.seoData?.slug, locale, regionDetails])

  // Set up batched analytics for product card views (same as BFF collection page)
  const collectionSlug = getFormattedSlug(plData.meta?.slug ?? '')
  const productViewTracker = `${collectionSlug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}`
  const pageMetaData = {
    slug: pageMeta.pageMetaData?.slug ?? plData.meta?.slug ?? pageMeta.pathname,
    title: pageMeta.pageMetaData?.title ?? plData.meta?.title,
    pathSelectedSubcollection: pageMeta.pageMetaData?.pathSelectedSubcollection,
  }
  const combinedBaseSortInput = serverSideFeatureFlags?.clpRankingBaseSortBy ?? pageMeta.baseSortBy
  const baseSortBy = useMemo(() => resolveClpRankingDefaultBaseSortBy(combinedBaseSortInput), [combinedBaseSortInput])

  const pageMetaWithTracking: PLPageMeta = {
    ...pageMeta,
    // Prefer live zip when the hook is active (`undefined` = feature off → keep SSR pageMeta).
    // Explicit `null` means no zip this session — do not fall back to a stale SSR/IP value.
    zipCode: zipCode !== undefined ? zipCode : pageMeta.zipCode,
    baseSortBy,
    productViewTracker,
    pageMetaData,
    subscriptionExpEnabled: isRecurringItemSubscriptionsExpEnabled,
  }

  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData,
  })

  useEffect(() => {
    if (!isMounted || getFormattedSlug(plData.meta?.slug ?? '') !== getFormattedSlug(pathname)) {
      const eventData = getCollectionViewData(plData, { zipCode })

      trackEvent(TELEMETRY_EVENT_NAMES.COLLECTION_VIEW, eventData)

      setFindingMethod[FINDING_METHOD_AGGREGATE.LANDING_PAGE]?.()
    }
    // TODO: This is just for events, and we want them to fired on only pathname and searchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, query])

  useEffect(() => {
    if (consumePendingCollectionProductScroll(pathname)) {
      scrollToProductSection(isMobile)
    }
  }, [pathname, isMobile])

  // Get cached states to restore into page-scoped store
  const cacheKey = getCollectionCacheKey(pageMeta.pathname, pageMeta.searchParams)
  const cachedData = collectionCacheStateManager.getCollectionCache(cacheKey)
  const initialWidgetStates = cachedData?.widgetPaginationStates
  const initialLayoutPaginationState = cachedData?.layoutPaginationState

  // Extract layout pagination from plData
  const initialPagination = plData.pagination

  const schemaPageName = resolveSchemaPageName(pageMetaWithTracking)
  const collectionPageSchemaProps = useMemo(() => {
    if (!serverSideFeatureFlags?.enableCollectionPageSchema) return null
    // JSON-LD is built from the SSR pageMeta on purpose: crawlers only ever land on a URL
    // (filtered or unfiltered) and never apply/clear filters client-side. The SSR path stamps
    // canonicalFilterSuffix + isV2Format onto pageMeta, so buildPageUrl emits the same filtered
    // url/@id the crawler's canonical + og:url use. In-session client-side filter changes don't
    // need to update the schema since no crawler observes them.
    return buildCollectionPageSchemaPropsPL(plData, pageMetaWithTracking, schemaPageName, {
      withName: t('clp_schema_description_with_name', {
        pageName: schemaPageName,
        defaultValue: `Shop ${schemaPageName} at Quince`,
      }),
      default: t('clp_schema_description_default', { defaultValue: 'Shop at Quince' }),
    })
  }, [plData, pageMetaWithTracking, serverSideFeatureFlags?.enableCollectionPageSchema, schemaPageName, t])

  return (
    <PLPageProvider
      key={providerKey}
      pageMeta={pageMetaWithTracking}
      initialPageStates={initialWidgetStates}
      initialLayoutPaginationState={initialLayoutPaginationState}
      enableScrollRestoration={true}
    >
      {/* JSON-LD rendered inline (not in <Head>) to match the existing
          codebase convention used by BreadcrumbSchema and FaqJsonLd. Both
          placements are equivalent for crawlers; consistency keeps SEO
          debugging predictable. */}
      {collectionPageSchemaProps && <CollectionPageSchema {...collectionPageSchemaProps} />}

      <ResponsiveInteractionsProvider optimisticPaint={optimisticPaint}>
        <FilterSortProvider onSeoDataUpdate={isPathSubcollectionMode ? undefined : onSeoDataUpdate}>
          {!isPathSubcollectionMode && dynamicSeoMetadata && (
            <SeoPageRouter
              seoMetaData={dynamicSeoMetadata}
              alternateUrls={pageMeta.alternateUrls}
              regionDetails={regionDetails}
              isI18nPreviewEnabled={isI18nPreviewEnabled}
            />
          )}
          <CLPPageHooks pageMeta={pageMetaWithTracking} />
          {/* CLPLayout is a separate component because its widgets read
            CollectionFeatureProvider context, which must be available above them */}
          <CLPLayout
            plData={plData}
            pageMeta={pageMetaWithTracking}
            widgetRegistry={widgetRegistry}
            initialPagination={initialPagination}
          />
        </FilterSortProvider>
      </ResponsiveInteractionsProvider>
    </PLPageProvider>
  )
}
